import React, { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { selectCurrentEmployee, setCurrentSite } from 'store/App';
import { selectCurrentEmployeeManagedSites, selectIsPermitted } from 'store/Employers';
import { EmployeeRole, IEmployerSite } from 'types';
import { getSiteSwitcherInfo } from 'utils';
import SiteSelector from './SiteSelector';
import { SiteSelectorAppearance } from './types';

interface IConnectedSiteSelector {
  appearance?: SiteSelectorAppearance;
  allSitesEnabled?: boolean;
  onSiteSelect?: (site: IEmployerSite) => void;
}

const SiteSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(theme.space.xl)};

  @media (min-width: ${theme.breakpoint.small}px) {
    align-items: flex-start;
    width: auto;
    margin-bottom: 0;
  }
`;

const ConnectedSiteSelector = ({ appearance = SiteSelectorAppearance.DEFAULT, allSitesEnabled, onSiteSelect }: IConnectedSiteSelector) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const currentEmployee = useSelector(selectCurrentEmployee());
  // This list is rebuilt every time it is read. `shallowEqual` checks for the sites inside it, so the page only redraws when they really change.
  const currentEmployeeSites = useSelector(selectCurrentEmployeeManagedSites, shallowEqual);

  const handleSiteSelect = (selected: IEmployerSite) => {
    dispatch(setCurrentSite(selected?.id));
  };

  const allSitesRollupPermitted = useSelector(selectIsPermitted([EmployeeRole.ADMIN]));
  const siteSwitcherInfo = getSiteSwitcherInfo(currentEmployeeSites || [], currentEmployee?.siteId);

  // On mount, select the current (or first) site to reload its data; not via onSiteSelect, which is for a user's pick.
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;

      const site = siteSwitcherInfo.currentSite || siteSwitcherInfo.options[0];
      if (site) handleSiteSelect(site);
    }
  }, [dispatch, siteSwitcherInfo]);

  return (
    <SiteSelectorContainer>
      {siteSwitcherInfo.options && (
        <SiteSelector options={siteSwitcherInfo.options} currentSite={siteSwitcherInfo.currentSite} onChange={onSiteSelect || handleSiteSelect} appearance={appearance} allSitesEnabled={allSitesEnabled && allSitesRollupPermitted} />
      )}
    </SiteSelectorContainer>
  );
};

export default ConnectedSiteSelector;
